import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { ActiveStatus, PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { useQueryClient } from 'react-query';
import { CallIntelligenceWritebackV1, CallIntelTypes } from '@frontend/api-call-intel';
import { PetsV1 } from '@frontend/api-pets';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { SchemaQueryKey } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { sentry } from '@frontend/tracking';
import {
  Chip,
  PopoverDialog,
  SpinningLoader,
  useAlert,
  useModalControl,
  usePopoverDialog,
} from '@frontend/design-system';
import { useCallIntelPmsSyncConfig } from '../../hooks/use-call-intel-pms-sync-config';
import { generateTrackingId } from '../../utils/tracking';
import { SummarySyncPopoverContent } from './summary-sync-popover-content';
import { SyncPetsModal } from './sync-pets-modal';
import { getAggregatedSyncStats, getPetPmsServiceableId, getSummarySyncChipConfig } from './utils';

interface SummarySyncChipProps {
  callRecord?: Pick<CallIntelTypes.CallRecord, 'id' | 'person' | 'locationId'>;
  isDemo?: boolean;
}

export const SummarySyncChip: FC<SummarySyncChipProps> = ({ callRecord, isDemo }) => {
  const alerts = useAlert();
  const { t } = useTranslation('call-intelligence');
  const [manualSyncInProgress, setManualSyncInProgress] = useState(false);
  const { accessibleLocationData } = useAppScopeStore();
  const { modalProps: syncPetsModalProps, openModal: openSyncPetsModal } = useModalControl();
  const queryClient = useQueryClient();
  const checkManualSyncStatusRef = useRef(false);

  const { isContactPresent, summarySyncStatusResponse, summarySyncMode, isFetchingSummarySync } =
    useCallIntelPmsSyncConfig({
      call: callRecord,
      fetchSyncStatus: true,
      isDemo,
    });

  const isProdEnv = appConfig.BACKEND_API.includes('api.weaveconnect.com');
  const isWeaveUserInDevEnv = !isProdEnv && isWeaveUser();
  const hasManualSyncAccess =
    isDemo ||
    isWeaveUserInDevEnv ||
    hasSchemaACL(callRecord?.locationId || '', Permission.CALL_INTELLIGENCE_WRITEBACK_MANUAL_SYNC);

  const selectedLocationId = callRecord?.locationId ?? '';
  const isVetVertical = accessibleLocationData[selectedLocationId]?.vertical === Vertical.VET;

  const { data: petsResponse, isLoading: isPetsLoading } = PetsV1.Queries.useFetchPetsByPersonIdQuery(
    {
      personId: callRecord?.person?.id || '',
      locationId: selectedLocationId,
      // @ts-expect-error - isDemo is used internally by demo mock router
      isDemo,
    },
    {
      enabled: isVetVertical && !!callRecord?.person?.id,
    }
  );

  const petPmsIdToDetailsMap = useMemo<Record<string, PetDetails>>(() => {
    return (
      petsResponse?.pets?.reduce<Record<string, PetDetails>>((acc, current) => {
        const pmsId = getPetPmsServiceableId(current);
        // Filters out pets without a serviceable PMS ID or that are not active, as they are not eligible for sync
        if (pmsId && current.activeStatus === ActiveStatus.ACTIVE) {
          acc[pmsId] = current;
        }
        return acc;
      }, {}) || {}
    );
  }, [petsResponse?.pets]);

  const activeCallWritebacks = useMemo(() => {
    if (isVetVertical) {
      // For vet vertical, only consider writeback status of pets that are eligible for sync based on PMS serviceable ID and active status
      return summarySyncStatusResponse?.callWritebacks?.filter((writebackStatus) => {
        return !writebackStatus.serviceablePmsId || petPmsIdToDetailsMap?.[writebackStatus.serviceablePmsId];
      });
    }

    return summarySyncStatusResponse?.callWritebacks;
  }, [summarySyncStatusResponse?.callWritebacks, isVetVertical, petPmsIdToDetailsMap]);

  const unsyncedPetsList = useMemo<PetDetails[]>(() => {
    if (!activeCallWritebacks || !petPmsIdToDetailsMap) {
      return [];
    }

    return Object.entries(petPmsIdToDetailsMap)
      .filter(([pmsId]) => {
        return !activeCallWritebacks?.some(
          (writebackStatus) =>
            writebackStatus.serviceablePmsId === pmsId &&
            writebackStatus.status === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED
        );
      })
      .map(([_, petDetails]) => petDetails);
  }, [petPmsIdToDetailsMap, activeCallWritebacks]);

  const { mutateAsync: initiateManualWritebackMutateAsync } =
    CallIntelligenceWritebackV1.Mutations.useWritebackCallsMutation();

  const isSyncing = manualSyncInProgress || isFetchingSummarySync;

  const summarySyncChipConfig = useMemo(() => {
    return getSummarySyncChipConfig({
      writebackStatusList: activeCallWritebacks ?? [],
      isContactMissing: !isContactPresent,
      isSyncing,
      hasManualSyncAccess,
    });
  }, [isContactPresent, activeCallWritebacks, hasManualSyncAccess, isSyncing]);

  const {
    getDialogProps,
    getTriggerProps,
    open: openPopover,
    close: closePopover,
  } = usePopoverDialog({ placement: 'bottom' });

  const triggerManualSync = useCallback(
    async (serviceablePmsIdList: string[] = []) => {
      if (!isDemo && !hasManualSyncAccess) return;
      setManualSyncInProgress(true);

      const baseRequest: CallIntelligenceWritebackV1.Types.WritebackCallsIO['input'] = {
        callId: callRecord?.id || '',
        locationId: callRecord?.locationId || '',
        isDemo,
      };

      // No PMS IDs means a single request without serviceablePmsId
      const requests =
        serviceablePmsIdList.length === 0
          ? [baseRequest]
          : serviceablePmsIdList.map((serviceablePmsId) => ({ ...baseRequest, serviceablePmsId }));

      const results = await Promise.allSettled(requests.map((req) => initiateManualWritebackMutateAsync(req)));

      const failedResults = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map((result) => result.reason);
      const failureCount = failedResults.length;
      const totalCount = results.length;

      if (failureCount) {
        const errorMessage =
          failureCount < totalCount
            ? t('{{count}} sync(s) failed to initiate. Please try again.', { count: failureCount })
            : t('Failed to initiate sync. Please try again.');
        alerts.error(errorMessage);
        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: `PMS Summary Sync Error: ${failureCount} of ${totalCount} failed`,
          addContext: {
            name: 'PMS Summary Sync Error',
            context: {
              callId: callRecord?.id,
              failureCount,
              failedResults,
              totalCount,
            },
          },
        });
      }

      if (failureCount < totalCount) {
        checkManualSyncStatusRef.current = true;
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as SchemaQueryKey<
              CallIntelligenceWritebackV1.Types.ServiceQueries,
              'CallWritebackStatusV2'
            >;
            return (
              Array.isArray(queryKey) &&
              queryKey[0] === CallIntelligenceWritebackV1.serviceName &&
              queryKey[1] === 'CallWritebackStatusV2' &&
              queryKey[2]?.callId === callRecord?.id
            );
          },
        });
      }
      setManualSyncInProgress(false);
    },
    [
      alerts,
      callRecord?.id,
      callRecord?.locationId,
      hasManualSyncAccess,
      initiateManualWritebackMutateAsync,
      isDemo,
      queryClient,
      t,
    ]
  );

  const handleChipClick = useCallback(() => {
    if (summarySyncChipConfig.isLoading) {
      return;
    }
    switch (summarySyncChipConfig.aggregatedWritebackStatus) {
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_READY_TO_SYNC:
        {
          if (summarySyncChipConfig.disabled || (isVetVertical && unsyncedPetsList.length === 0)) {
            return;
          }
          if (!isVetVertical) {
            triggerManualSync();
          } else if (unsyncedPetsList.length > 1) {
            openSyncPetsModal();
          } else {
            triggerManualSync(unsyncedPetsList.map(getPetPmsServiceableId));
          }
        }

        break;
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED:
      case CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED:
        // Open popover to show status details
        openPopover();
        break;
      default:
        break;
    }
  }, [
    isVetVertical,
    openPopover,
    openSyncPetsModal,
    summarySyncChipConfig.aggregatedWritebackStatus,
    summarySyncChipConfig.disabled,
    summarySyncChipConfig.isLoading,
    triggerManualSync,
    unsyncedPetsList,
  ]);

  const handleSyncAdditionalPets = useCallback(() => {
    closePopover();
    openSyncPetsModal();
  }, [closePopover, openSyncPetsModal]);

  const handleRetryManualSync = useCallback(
    (serviceablePmsIdList?: string[]) => {
      closePopover();
      triggerManualSync(serviceablePmsIdList);
    },
    [closePopover, triggerManualSync]
  );

  useEffect(() => {
    if (!checkManualSyncStatusRef.current || isSyncing || !activeCallWritebacks) {
      return;
    }

    const { failedStatusCount, totalStatuses, aggregatedStatus } = getAggregatedSyncStats(activeCallWritebacks);
    if (aggregatedStatus === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_SUCCEEDED) {
      alerts.success(t('Summary synced successfully to PMS.'));
    } else if (aggregatedStatus === CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_FAILED) {
      alerts.error(
        failedStatusCount < totalStatuses
          ? t('Summary sync partially failed. Please try again.')
          : t('Summary sync failed. Please try again.')
      );
    }
    checkManualSyncStatusRef.current = false;
  }, [alerts, isSyncing, t, activeCallWritebacks]);

  if (!activeCallWritebacks?.length || !summarySyncChipConfig.aggregatedWritebackStatus || isPetsLoading) {
    return null;
  }

  return (
    <>
      <Chip<'button'>
        leftElement={<SyncIcon iconName={summarySyncChipConfig.iconName} />}
        variant={summarySyncChipConfig.chipVariant}
        maxWidth='240px'
        as='button'
        css={{
          cursor: summarySyncChipConfig.disabled ? 'default' : 'pointer',
          border: `1px solid ${summarySyncChipConfig.borderColor}`,
          backgroundColor: summarySyncChipConfig.backgroundColor,
          color: summarySyncChipConfig.color,
        }}
        {...getTriggerProps()}
        trackingId={generateTrackingId({
          component: 'summary-sync-chip',
          context: 'chip-click',
          isDemoAccount: isDemo,
        })}
        onClick={handleChipClick}
        tooltipLabel={summarySyncChipConfig.tooltip}
        disabled={summarySyncChipConfig.disabled}
        data-testid='summary-sync-chip'
      >
        {summarySyncChipConfig.label}
      </Chip>
      <PopoverDialog {...getDialogProps()} css={styles.popoverContainer}>
        <SummarySyncPopoverContent
          callWritebacks={activeCallWritebacks}
          summarySyncMode={summarySyncMode}
          onRetrySync={handleRetryManualSync}
          isDemo={isDemo}
          hasManualSyncAccess={hasManualSyncAccess}
          isSyncing={isSyncing}
          hasUnsyncedPets={unsyncedPetsList.length > 0}
          petPmsIdToDetailsMap={petPmsIdToDetailsMap}
          openSyncPetsModal={handleSyncAdditionalPets}
        />
      </PopoverDialog>
      <SyncPetsModal
        {...syncPetsModalProps}
        petList={unsyncedPetsList}
        isDemo={isDemo}
        onSyncClick={triggerManualSync}
      />
    </>
  );
};

export const SyncIcon: FC<{ iconName: IconName | 'loader' }> = ({ iconName }) => {
  if (iconName === 'loader') {
    return <SpinningLoader size='xxs' />;
  }
  return <Icon name={iconName} />;
};

const styles = {
  popoverContainer: css`
    padding: 0;
    min-width: 250px;
    max-width: 350px;
  `,
};
